import { ArrowLeft } from 'lucide-react';
import { Link, Outlet } from 'react-router';

import { PreDashboardI18n } from '@/features/auth/components/PreDashboardI18n';
import { AdminLanguageMenu } from '@/features/i18n/components/AdminLanguageMenu';
import { useT } from '@/lib/i18n';

/**
 * Sign in, sign up, the email code, password resets and invitations - in the
 * language picked at the top of the card (`PreDashboardI18n`), so a merchant
 * who reads Hindi does not have to get through English to reach a dashboard
 * that speaks it.
 */
export function AuthLayout() {
  return (
    <PreDashboardI18n>
      <AuthFrame />
    </PreDashboardI18n>
  );
}

function AuthFrame() {
  const t = useT();
  return (
    // The backdrop is opaque and covers the viewport, which is what hides the
    // site-wide `body::before` wash underneath it: two illustrations stacked
    // would read as neither.
    //
    // No `BrandLogo` here. Both cuts of the artwork carry the NeonCarts lockup
    // in their own top-left corner, and `auth-backdrop` sizes them so it is in
    // frame on every viewport, so a rendered one would be the second logo on the
    // screen rather than the first.
    <div className="auth-backdrop flex min-h-dvh flex-col items-center justify-center px-5 py-8">
      {/* A page marked `data-auth-wide` (sign-up) gets room for two columns,
          which is what keeps its longer form on a single screen. */}
      <div className="w-full max-w-md has-[[data-auth-wide]]:max-w-2xl">
        {/* The way out. Everything else on these screens moves you further into
            signing in, and the header that would normally carry a route home is
            not rendered here. It is aligned to the card's left edge rather than
            the centred column so it reads as a step back from the form rather
            than a caption for it. */}
        <div className="mb-3 flex items-center justify-between gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/75 px-3 py-1.5 text-sm font-medium text-ink-soft backdrop-blur-sm transition-colors hover:bg-white hover:text-ink"
          >
            <ArrowLeft className="size-4 rtl-flip" aria-hidden />
            {t('auth.layout.back_home')}
          </Link>
          <div className="rounded-lg bg-white/75 backdrop-blur-sm">
            <AdminLanguageMenu />
          </div>
        </div>

        <div className="rounded-card border border-line bg-white p-7 shadow-lift">
          <Outlet />
        </div>
      </div>

      {/* Muted grey has nothing to hold onto over a photograph, so the strap
          line and the link above both sit on a plate of their own. */}
      <p className="mt-6 rounded-full bg-white/75 px-3 py-1.5 text-xs text-ink-soft backdrop-blur-sm">
        {t('auth.layout.strapline')}
      </p>
    </div>
  );
}
